import type { AttendeeStudiedSessions } from "@verity-connect/contracts";
import { useEffect, useState } from "react";
import { Link } from "./router";

/**
 * Asking for a seminar she could not attend.
 *
 * Ten run at once and she sat in one, so the convention itself took the other nine from her.
 * This is the repair Decision 10 approved, in the shape Decision 97 settled: she asks, one
 * session at a time, and every request is written down against her name. Nobody approves it —
 * a queue somebody has to work on the two days that matter most is a queue nobody works.
 *
 * **It draws nothing unless it can do something.** The answer carries whether WVCEA has opened
 * this at all, so the button never appears where it would be refused. Offering a teacher
 * something and then saying no tells her twice that the software does not know its own mind.
 *
 * **It never says what she would get instead of hours.** The wording is about material, never
 * about credit, because the voucher is an attendance document and the software holds that line
 * elsewhere — in the data, where a teacher cannot be expected to remember it.
 */
export function StudySessionControl({ eventSlug, itemId, signedIn, saved, creditable }: {
  eventSlug: string;
  itemId: string;
  signedIn: boolean;
  /** She chose it, so it is already hers by the ordinary door. */
  saved: boolean;
  /** Seminars only. There is nothing in lunch or the preaching for a record to be built from. */
  creditable: boolean;
}) {
  const [state, setState] = useState<AttendeeStudiedSessions | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    if (!signedIn || saved || !creditable) return;
    const controller = new AbortController();
    void fetch(`/api/attendee/events/${encodeURIComponent(eventSlug)}/studied-sessions`, {
      credentials: "same-origin",
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return;
      setState(await response.json() as AttendeeStudiedSessions);
    }).catch(() => {
      // Silence is the right failure. A session page that still works is better than one
      // carrying an error about a feature she never asked for.
    });
    return () => controller.abort();
  }, [eventSlug, itemId, signedIn, saved, creditable]);

  if (!signedIn || saved || !creditable || !state?.open) return null;

  const already = state.itemIds.includes(itemId);

  const ask = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      const response = await fetch(
        `/api/attendee/events/${encodeURIComponent(eventSlug)}/sessions/${encodeURIComponent(itemId)}/study`,
        { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: "{}" },
      );
      if (response.ok) {
        setState({ ...state, itemIds: [...state.itemIds, itemId] });
        return;
      }
      const body = await response.json().catch(() => null) as { message?: string } | null;
      // The server's own words. It knows which of the several reasons applies and this does
      // not, and a second wording here would drift away from the first.
      setRefusal(body?.message ?? "That could not be asked for just now.");
    } finally {
      setBusy(false);
    }
  };

  if (already) {
    return (
      <section className="session-study" aria-label="A session you missed">
        <p className="session-study-have">This one is yours to study.</p>
        <Link className="session-study-open" href={`/my-wvcea/learning/${encodeURIComponent(itemId)}/transcript`}>
          Read the transcript
        </Link>
      </section>
    );
  }

  return (
    <section className="session-study" aria-label="A session you missed">
      <p>You were here for this slot but chose another seminar. You can have this one's material.</p>
      <button type="button" className="session-study-ask" disabled={busy} onClick={() => void ask()}>
        {busy ? "Asking…" : "Ask for this one"}
      </button>
      {refusal && <p className="session-study-refusal">{refusal}</p>}
    </section>
  );
}
